/**
 * DEV-2503. Справочник менеджеров — один на вкладку: client-data GetManagers (только
 * role=manager) ∪ auth by-roles (hos, ceo). Раньше каждый из двух десятков вызовов
 * useManagers грузил его сам: by-roles уходил 57 раз за 19 минут работы менеджера.
 *
 * Живёт 30 минут: в обычной сессии это один запрос, а вкладка, открытая на весь день,
 * всё же увидит нового менеджера. Сбрасывается при выходе и входе (auth.store), а также
 * после блокировки и удаления пользователя (adminUsers.store, DEV-2576): справочник
 * отдаёт только активных (ADR-0068).
 * Неполный ответ (лёг один из источников) не запоминается — следующий вызов спросит снова.
 */
import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { V1ManagerInfo } from '@/api/clients/client'
import { apiClient, clientDataApi } from '@/api'
import { useSectionsStore } from '@/features/auth/stores/sections.store'

export const MANAGERS_TTL_MS = 30 * 60_000

/**
 * DEV-195. Сокращённая запись пользователя из auth.GetUsersByRoles
 * (GET /api/v1/auth/users/by-roles?role_keys=hos&role_keys=ceo).
 * Не реэкспортится из generated-клиента — auth API мы не генерим в TS, ходим
 * прямым axios-вызовом.
 */
interface AuthUserResponse {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  // первое поле, которое реально приходит в snake_case из gRPC-gateway, на случай если openapi camelCase не подхватился
  first_name?: string
  last_name?: string
}

const mapAuthUserToManagerInfo = (u: AuthUserResponse): V1ManagerInfo => ({
  id: u.id,
  email: u.email,
  firstName: u.firstName ?? u.first_name,
  lastName: u.lastName ?? u.last_name,
})

export const useManagersStore = defineStore('managers', () => {
  const managers = ref<V1ManagerInfo[]>([])
  let loadedAt = 0
  let inFlight: Promise<V1ManagerInfo[]> | null = null
  // Поколение: invalidate его меняет, и ответ прежней сессии не пишется.
  let generation = 0

  const fetchManagers = async () => {
    // DEV-1496: справочник закрыт правом users:read (DEV-1298) — без права не
    // спрашиваем, чтобы 403 не поднимал тост «права изменились» на карточках.
    const withDirectory = useSectionsStore().hasPermission('users:read')
    const [managersResp, hosCeoResp] = await Promise.allSettled([
      withDirectory
        ? clientDataApi.clientDataServiceGetManagers().then((resp) => resp.data.managers || [])
        : Promise.resolve<V1ManagerInfo[]>([]),
      // На auth.GetUsersByRoles роли репитятся как ?role_keys=hos&role_keys=ceo,
      // axios сам сериализует массив таким образом.
      apiClient.get<{ users?: AuthUserResponse[] }>('/api/v1/auth/users/by-roles', {
        params: { role_keys: ['hos', 'ceo'] },
        paramsSerializer: { indexes: null },
      }),
    ])

    const base = managersResp.status === 'fulfilled' ? managersResp.value : []
    const hosCeo =
      hosCeoResp.status === 'fulfilled'
        ? (hosCeoResp.value.data.users || []).map(mapAuthUserToManagerInfo)
        : []

    // Merge by id — менеджеры имеют приоритет (более полный профиль из client-data).
    const byId = new Map<string, V1ManagerInfo>()
    for (const u of [...hosCeo, ...base]) {
      if (u.id) byId.set(u.id, u)
    }

    const complete = hosCeoResp.status === 'fulfilled' && managersResp.status === 'fulfilled'
    return { list: Array.from(byId.values()), complete }
  }

  /** Справочник: из памяти, пока свежий; параллельные вызовы делят один запрос. */
  const load = (): Promise<V1ManagerInfo[]> => {
    if (loadedAt && Date.now() - loadedAt < MANAGERS_TTL_MS) return Promise.resolve(managers.value)

    const mine = generation
    inFlight ??= fetchManagers()
      .then(({ list, complete }) => {
        if (mine !== generation) return list
        // Неполный ответ (один источник лёг) не держим полчаса — спросим снова — и не
        // затираем им полный список, если он уже был.
        if (complete || !managers.value.length) managers.value = list
        loadedAt = complete ? Date.now() : 0
        return managers.value
      })
      .finally(() => {
        if (mine === generation) inFlight = null
      })

    return inFlight
  }

  const invalidate = () => {
    generation += 1
    managers.value = []
    loadedAt = 0
    inFlight = null
  }

  return { managers, load, invalidate }
})
